/**
 * Local icon set for the Magic Capture prototype.
 *
 * The llama Icon atom's curated FontAwesome set has no camera / sparkle /
 * gallery glyphs, and adding a dependency would touch the root lockfile
 * (outside prototype scope). These are inline Lucide icons (ISC licence),
 * local to the prototype only.
 */
import React from 'react';

interface LocalIconProps {
  size?: number;
  color?: string;
  strokeWidth?: number;
}

const base = ({
  size = 24,
  color = 'currentColor',
  strokeWidth = 2,
}: LocalIconProps) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: color,
  strokeWidth,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

export const CameraIcon = (p: LocalIconProps) => (
  <svg {...base(p)}>
    <path d='M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z' />
    <circle cx='12' cy='13' r='3' />
  </svg>
);

export const GalleryIcon = (p: LocalIconProps) => (
  <svg {...base(p)}>
    <rect width='18' height='18' x='3' y='3' rx='2' ry='2' />
    <circle cx='9' cy='9' r='2' />
    <path d='m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21' />
  </svg>
);

export const FileUpIcon = (p: LocalIconProps) => (
  <svg {...base(p)}>
    <path d='M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z' />
    <path d='M14 2v4a2 2 0 0 0 2 2h4' />
    <path d='M12 12v6' />
    <path d='m15 15-3-3-3 3' />
  </svg>
);

export const SparklesIcon = (p: LocalIconProps) => (
  <svg {...base(p)}>
    <path d='m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z' />
    <path d='M5 3v4' />
    <path d='M19 17v4' />
    <path d='M3 5h4' />
    <path d='M17 19h4' />
  </svg>
);

export const FlagIcon = (p: LocalIconProps) => (
  <svg {...base(p)}>
    <path d='M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z' />
    <line x1='4' x2='4' y1='22' y2='15' />
  </svg>
);

export const CheckIcon = (p: LocalIconProps) => (
  <svg {...base(p)}>
    <path d='M20 6 9 17l-5-5' />
  </svg>
);

export const XIcon = (p: LocalIconProps) => (
  <svg {...base(p)}>
    <path d='M18 6 6 18' />
    <path d='m6 6 12 12' />
  </svg>
);

export const PencilIcon = (p: LocalIconProps) => (
  <svg {...base(p)}>
    <path d='M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z' />
    <path d='m15 5 4 4' />
  </svg>
);

export const ChevronLeftIcon = (p: LocalIconProps) => (
  <svg {...base(p)}>
    <path d='m15 18-6-6 6-6' />
  </svg>
);

export const RetryIcon = (p: LocalIconProps) => (
  <svg {...base(p)}>
    <path d='M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8' />
    <path d='M3 3v5h5' />
  </svg>
);
